/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */

import { LitElement, html, CSSResultGroup } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "./components/dev-console-product-item.js"
// import "whatwg-fetch"
import { globalStyles } from "./styles";
import { gridSvg } from "./assets/svg-ts/grid";
import { products } from "./staticData/products";
import { nameAndDesc, utilities, technicalDocs} from "./components/dev-console-product-details"
/**
 * @fires count-changed - Indicates when the count changes
 * @slot - This element has a slot
 * @csspart button - The button
 */
@customElement("dev-console-widget")
export class DevConsoleWidget extends LitElement {
  static override styles?: CSSResultGroup | undefined = [globalStyles];

  outsideClickListener = (event:Event) =>{
    if (event.target !== this) {
      this.isOpen = false
    }
  }
  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.outsideClickListener);
    this.fetchData();
  }

  override disconnectedCallback(): void {
      super.disconnectedCallback();
      document.removeEventListener('click',this.outsideClickListener)
  }

  @property()
  iconHeight = "32px";

  @property()
  iconWidth = "32px";

  @property()
  iconColor = "white";

  @property()
  isMobile = false;

  @state()
  activeProductKey = "apex";

  @state()
  isOpen = false;

  @state()
  productDetailsMap: any = {};

  @state()
  mobileClosedProduct: string = "";

  async fetchData() {
    const res = await fetch(
      `https://dev.assets.developer.tech.gov.sg/products-details/${this.activeProductKey}.json`
    );
    const data = await res.json();

    this.productDetailsMap = {
      ...this.productDetailsMap,
      [this.activeProductKey]: data,
    };
  }

  getActiveProductObj(){
    return this.productDetailsMap[this.activeProductKey]
  }

  shouldDisplayAccordion(productKey:string){
    return this.activeProductKey === productKey && productKey !== this.mobileClosedProduct
  }

  desktopView(){
    return html`
      <div
        class="hidden md:grid grid-flow-col grid-cols-1 md:grid-cols-8 gap-0 divide-x text-sm"
      >
        <div class="col-span-3 lg:col-span-2 py-6 h-widget-dropdown overflow-y-auto">
          <div class="flex flex-col space-y-2">
            <strong class="pl-6">Browse Products</strong>
            <ul class="list-none flex-col">
              ${
                products.map(
                  (product) => html`
                    <dev-console-list-item 
                      activeProductKey=${this.activeProductKey}
                      .product=${product}
                    ></dev-console-list-item>
                  `
                )
              }
            </ul>
          </div>
        </div>
        ${this.productDetailsMap[this.activeProductKey]
          ? html`
              <div class="col-span-5 lg:col-span-6 py-6 px-8 h-widget-dropdown">
                <div class="hidden md:block">
                  <div class="grid grid-cols-7 gap-20">
                    <div class="col-span-7 lg:col-span-5 flex flex-col space-y-6">
                      ${nameAndDesc(this.getActiveProductObj())}
                      ${technicalDocs(this.getActiveProductObj())}
                    </div>
                    <div class="col-span-7 lg:col-span-2">
                      ${utilities(this.getActiveProductObj())}
                    </div>
                  </div>
                </div>
              </div>
            `
          : html` <span>Loading...</span> `}
      </div>
    `
  }

  mobileView(){
    return html`
      <div class="md:hidden h-screen w-full overflow-y-auto" >
        <div class="col-span-1 py-6">
          <div class="flex flex-col space-y-2">
            <strong class="pl-6">Browse Products</strong>
            <ul class="list-none flex-col">
              ${
                products.map(
                  (product) => html`
                    <dev-console-list-item 
                      isMobile="true" 
                      activeProductKey=${this.activeProductKey}
                      mobileClosedProduct=${this.mobileClosedProduct}
                      .product=${product}
                    >
                      <div class="flex flex-col pl-14 pr-4 pb-8 justify-start space-y-6 md:hidden" slot="product-details">
                          ${nameAndDesc(this.getActiveProductObj())}
                          ${technicalDocs(this.getActiveProductObj())}
                          ${utilities(this.getActiveProductObj())}
                      </div>                    
                    </dev-console-list-item>
                  `
                )
              }
            </ul>
          </div>
        </div>
      </div>
    `
  }

  override render() {
    return html`
      <div class="h-full flex items-center cursor-pointer font-sans">
        <div
          style="color: ${this.iconColor}; width: ${this.iconWidth}; height: ${this.iconHeight};"
          @click=${() => this.isOpen = !this.isOpen}
        >
          ${gridSvg}
        </div>
        ${
          this.isOpen ? 
          html`
            <div
              class="absolute z-20 left-0 top-full w-full h-auto shadow-md cursor-default bg-white block border-t-2 border-solid border-dropdown-border"
              @itemSelect=${this._selectListener}
            >
              ${this.desktopView()}
              ${this.mobileView()}
            </div>
          ` : html``
        }
      </div>
    `;
  }

  private _selectListener(e: CustomEvent) {
    console.log("called", e)
    const selectedProductKey = e.detail.uniqueKey;
    const fromMobile = e.detail.fromMobile;

    // Accordion closing logic. An open tab can be closed in mobile but not in desktop mode.
    if(this.activeProductKey === selectedProductKey && fromMobile){
      if(this.mobileClosedProduct === ""){
        this.mobileClosedProduct = selectedProductKey;
      } else {
        this.mobileClosedProduct = "";
      }
    } else {
      this.activeProductKey = selectedProductKey;
      this.mobileClosedProduct = "";
    }
    if (!this.productDetailsMap[this.activeProductKey]) {
      this.fetchData();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "dev-console-widget": DevConsoleWidget;
  }
}
